import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import { mutateData } from "@/core/api/api-methods";
import {
  invalidateAfterMutation,
  payrollBatchCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { payrollEndpoints } from "@/features/payroll/api/endpoints";
import type { PayrollBatchRefreshResponseDto } from "@/features/payroll/api/dto";

/**
 * Relit l'état d'une paie `PROCESSING` auprès de wallet-service
 * (`POST /PayrollBatches/{id}/refresh`) : terminée (`COMPLETED` /
 * `PARTIALLY_COMPLETED` / `FAILED`), toujours en attente, ou jamais parvenue à
 * wallet-service — elle revient alors `SIMULATED`, rien n'ayant été débité.
 * Sans effet sur une paie dans un autre statut.
 */
export const useRefreshPayrollBatch = (batchId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["payroll", "payrollBatches", "refresh", batchId],
    mutationFn: () =>
      mutateData({
        url: payrollEndpoints.payrollBatches.refresh(batchId),
        method: "POST",
      }) as unknown as Promise<PayrollBatchRefreshResponseDto>,
    onSuccess: () =>
      invalidateAfterMutation(queryClient, payrollBatchCacheKeys(batchId)),
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useRefreshPayrollBatch failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "payroll" }
      );
    },
  });
};
